/**
 * Search Session Management Utility
 * Manages search_id for tracking user search sessions and related events
 */

const SEARCH_SESSION_KEY = 'ahafood.search_session_id';

/**
 * Generate search_id from customer_id and keyword
 * Format: {customer_id}_{keyword}
 */
export const generateSearchId = (
  customerId: string,
  keyword: string
): string => {
  const cleanKeyword = keyword.trim().toLowerCase().replace(/\s+/g, '-');
  return `${customerId}_"${cleanKeyword}"`;
};

/**
 * Store search_id in session storage
 */
export const setSearchSessionId = (searchId: string): void => {
  try {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SEARCH_SESSION_KEY, searchId);
    }
  } catch (error) {
    console.error('[SearchSession] Error storing search_id:', error);
  }
};

/**
 * Get current search_id from session storage
 */
export const getSearchSessionId = (): string | null => {
  try {
    if (typeof window !== 'undefined') {
      const searchId = sessionStorage.getItem(SEARCH_SESSION_KEY);
      console.log('[SearchSession] Retrieved search_id:', searchId);
      return searchId;
    }
  } catch (error) {
    console.error('[SearchSession] Error retrieving search_id:', error);
  }
  return null;
};

/**
 * Clear search_id from session storage
 */
export const clearSearchSessionId = (): void => {
  try {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(SEARCH_SESSION_KEY);
    }
  } catch (error) {
    console.error('[SearchSession] Error clearing search_id:', error);
  }
};

/**
 * Create and store new search session
 */
export const createSearchSession = (
  customerId: string,
  keyword: string
): string => {
  const searchId = generateSearchId(customerId, keyword);
  setSearchSessionId(searchId);
  return searchId;
};

/**
 * Update search session with new keyword (if customer_id matches)
 */
export const updateSearchSession = (
  customerId: string,
  keyword: string
): string => {
  const newSearchId = generateSearchId(customerId, keyword);
  setSearchSessionId(newSearchId);
  return newSearchId;
};

/**
 * Check if current search session belongs to the same customer
 */
export const isCurrentCustomerSession = (customerId: string): boolean => {
  const currentSearchId = getSearchSessionId();
  if (!currentSearchId || !customerId) return false;

  return currentSearchId.startsWith(`${customerId}_`);
};
